import { ClientMsgKind, computeRoleDistribution, type Locale } from '@infiltre/shared';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { selectRoom, useAmIHost, useCanStart, useCanUpdateOptions } from '@/entities/room';
import { useGameStore, useSessionStore } from '@/shared/hooks/use-shallow-store';
import { trackEvent } from '@/shared/lib/analytics';
import { useDialog } from '@/shared/lib/dialog';
import { useT } from '@/shared/lib/i18n';
import { type MugshotConfig, mugshotForPlayer } from '@/shared/lib/mugshot';
import { DossierBriefingPanel, HostSettingsSheet } from '@/shared/ui';
import { BriefingScreen, DossierTopBar, MobileDossierTopBar } from '@/shared/ui/briefing-screen';
import { SuspectBoard, type SuspectSlot } from '@/shared/ui/suspect-board';
import { MobileLobbyFooter } from './lobby-room/MobileLobbyFooter';
import { WaitingForHostPaper } from './lobby-room/WaitingForHostPaper';

type OptionsPatch = {
  includeClochard?: boolean;
  discussionSeconds?: number;
  infiltreCount?: number;
  locale?: Locale;
  hideCode?: boolean;
};

/**
 * Online lobby orchestrator — pulls live room/identity/driver out of the
 * store, computes derived flags (host? can start? settings open?), wires
 * mutators, and feeds the slots/aside/footer of `<BriefingScreen>` (the
 * shared cork-stage shell). The standalone setup uses the same shell —
 * see `StandaloneSetupPanel`.
 */
export const LobbyRoomView = () => {
  const t = useT();
  const navigate = useNavigate();
  const dialog = useDialog();
  const room = useGameStore(selectRoom);
  const code = useSessionStore((s) => s.roomCode);
  const identity = useSessionStore((s) => s.identity);
  const driver = useSessionStore((s) => s.driver);
  const isHost = useAmIHost();
  const canStart = useCanStart();
  const canEditSettings = useCanUpdateOptions();
  const setDriver = useSessionStore((s) => s.setDriver);
  const resetRoom = useGameStore((s) => s.resetRoom);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const enteredAtRef = useRef<number>(Date.now());

  const onBack = (): void => {
    // Send LeaveRoom before WS teardown so the server vacates the slot
    // immediately instead of routing through the disconnect grace timer.
    if (driver && identity && room) {
      try {
        driver.send({ kind: ClientMsgKind.LeaveRoom, actorPlayerId: identity.playerId });
        trackEvent({
          name: 'lobby.left_voluntary',
          data: { roomId: room.id, phase: room.phase },
        });
      } catch {
        // socket already torn down — close path handles vacate
      }
    }
    const finalize = (): void => {
      setDriver(null);
      resetRoom();
      void navigate({ to: '/' });
    };
    if (driver && identity) {
      // Macrotask gap lets the LeaveRoom frame flush before stop() closes the socket.
      setTimeout(finalize, 50);
    } else {
      finalize();
    }
  };

  const missing = !room || !code;
  useEffect(() => {
    if (missing) void navigate({ to: '/', replace: true });
  }, [missing, navigate]);
  if (missing) return null;

  const dist = computeRoleDistribution(
    Math.max(room.players.length, 3),
    room.options.includeClochard,
    room.options.infiltreCount,
  );
  const summaryChip = `${dist.infiltre}U${room.options.includeClochard ? '+W' : ''} · ${
    room.options.discussionSeconds === 0 ? '∞' : `${room.options.discussionSeconds}s`
  }`;

  const codeHidden = room.options.hideCode ?? false;
  const toggleCodeHidden = canEditSettings
    ? () => sendOptionsPatch({ hideCode: !codeHidden })
    : undefined;

  const sendOptionsPatch = (patch: OptionsPatch): void => {
    if (!identity || !driver) return;
    driver.send({
      kind: ClientMsgKind.UpdateRoomOptions,
      actorPlayerId: identity.playerId,
      options: patch,
    });
  };

  const startGame = (): void => {
    if (!identity || !driver) return;
    if (!canStart) return;
    trackEvent({
      name: 'lobby.start_clicked',
      data: {
        roomId: room.id,
        playerCount: room.players.length,
        lobbyMs: Date.now() - enteredAtRef.current,
      },
    });
    driver.send({ kind: ClientMsgKind.StartGame, actorPlayerId: identity.playerId });
  };

  const onSelfMugshotChange = (next: MugshotConfig): void => {
    if (!identity || !driver) return;
    driver.send({
      kind: ClientMsgKind.UpdateMugshot,
      actorPlayerId: identity.playerId,
      mugshot: next,
    });
  };

  const onSelfNameChange = (next: string): void => {
    if (!identity || !driver) return;
    driver.send({
      kind: ClientMsgKind.UpdateName,
      actorPlayerId: identity.playerId,
      name: next,
    });
  };

  const selfId = identity?.playerId ?? null;
  const slots: readonly SuspectSlot[] = room.players.map((p) => {
    const isSelf = p.id === selfId;
    return {
      id: p.id,
      name: p.name,
      mugshot: mugshotForPlayer(p),
      isHost: p.isHost,
      isSelf,
      onMugshotChange: isSelf ? onSelfMugshotChange : undefined,
      onNameChange: isSelf ? onSelfNameChange : undefined,
    };
  });

  const board = (
    <SuspectBoard
      slots={slots}
      maxSlots={25}
      header={t('online.lobby.briefing_room')}
      trailing={{ kind: 'wanted' }}
      analyticsMode="online"
      analyticsRoomId={room.id}
    />
  );

  const aside = isHost ? (
    <DossierBriefingPanel
      infiltreCount={dist.infiltre}
      onInfiltreCountChange={(n) => sendOptionsPatch({ infiltreCount: n })}
      includeClochard={room.options.includeClochard}
      onIncludeClochardChange={(v) => sendOptionsPatch({ includeClochard: v })}
      discussionSeconds={room.options.discussionSeconds}
      onDiscussionSecondsChange={(s) => sendOptionsPatch({ discussionSeconds: s })}
      locale={room.options.locale}
      onLocaleChange={(locale) => sendOptionsPatch({ locale })}
      playerCount={room.players.length}
      onStart={startGame}
      disabled={!canEditSettings}
    />
  ) : (
    <WaitingForHostPaper />
  );

  return (
    <>
      <BriefingScreen
        board={board}
        aside={aside}
        desktopTopBar={
          <DossierTopBar
            onBack={onBack}
            onShowRules={() => void dialog.open('rules', {})}
            code={code}
            codeHidden={codeHidden}
            onToggleCodeHidden={toggleCodeHidden}
          />
        }
        mobileHeader={
          <MobileDossierTopBar
            onBack={onBack}
            onShowRules={() => void dialog.open('rules', {})}
            code={code}
            codeHidden={codeHidden}
            onToggleCodeHidden={toggleCodeHidden}
          />
        }
        mobileFooter={
          <MobileLobbyFooter
            isHost={isHost}
            canStart={canStart}
            canEditSettings={canEditSettings}
            playerCount={room.players.length}
            summaryChip={summaryChip}
            onOpenSettings={() => setSettingsOpen(true)}
            onStart={startGame}
          />
        }
      />
      {canEditSettings && (
        <HostSettingsSheet
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          includeClochard={room.options.includeClochard}
          onIncludeClochardChange={(includeClochard) => sendOptionsPatch({ includeClochard })}
          discussionSeconds={room.options.discussionSeconds}
          onDiscussionSecondsChange={(discussionSeconds) => sendOptionsPatch({ discussionSeconds })}
          infiltreCount={dist.infiltre}
          onInfiltreCountChange={(next) => sendOptionsPatch({ infiltreCount: next })}
          locale={room.options.locale}
          onLocaleChange={(locale) => sendOptionsPatch({ locale })}
          playerCount={room.players.length}
        />
      )}
    </>
  );
};
